import {
  type ComponentType,
  type ReactNode,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { Link } from "react-router-dom";
import { brandService } from "@/shared/services/brandService";
import TableServer from "@/components/tables/TableServer";
import { makeFirstCharCap } from "@/helpers/utils";
import RpImageTextInline from "@/components/RpImageTextInline";
import useFetcher from "@/helpers/hooks/useFetcher";
import { MUICustomAutocompleteSelect } from "@/components/muiCustom/select/MUICustomAutocompleteSelect";
import { LoaderContext } from "@/helpers/contexts/contexts";
import { BRAND_CONST } from "@/config/constants";
import RpSvgIcon from "@/components/icons/RpSvgIcon";

type Brand = {
  id?: string | number;
};

type User = {
  id?: string | number;
};

type LocationFilters = {
  pageSize: number;
  pageIndex: number;
  searchKey: string;
  sortBy: string;
  sortByDir: string;
  groupId: string | number;
};

type FetchDataParams = {
  pageIndex: number;
  pageSize: number;
  searchKey?: string;
  filters?: Partial<LocationFilters>;
  sortBy?: string;
  sortByDir?: string;
};

type LocationRow = {
  location_name?: string;
  system_generated_location_id?: string | number;
  fk_participant_id?: string | number;
  participants_name?: string;
  getTotalCampaignActive?: string | number;
  getTotalCampaign?: string | number;
};

type GroupRecord = {
  id: string | number;
  group_name: string;
};

type GroupOption = {
  label: string;
  value: string | number;
};

type LocationListResponse = {
  result?: LocationRow[];
  pageCount?: number;
  recordsTotal?: number;
  groupResult?: GroupRecord[];
};

type ColumnCellProps = {
  row: {
    original: LocationRow;
  };
};

type TableColumn = {
  Header: ReactNode | (() => ReactNode);
  accessor: string;
  id?: string;
  width?: number;
  Cell?: (_props: ColumnCellProps) => ReactNode;
};

type TableServerProps = {
  columns: TableColumn[];
  data: LocationRow[];
  showSearch: boolean;
  fetchData: (_data: FetchDataParams) => void;
  isLoading: boolean;
  pageCount: number;
  filters: (
    _setFilter: (_key: keyof LocationFilters, _value: string | number) => void,
    _selectedValues?: Partial<LocationFilters>
  ) => ReactNode;
  useLayout: boolean;
  hideSearchLabel: boolean;
  totalRecords: number;
};

type AutocompleteProps = {
  options: GroupOption[];
  selectProps: {
    onChange: (_value: string | number) => void;
    value?: string | number;
  };
  name: string;
  label: string;
  className: string;
  size: string;
  all: boolean;
};

type ImageTextInlineProps = {
  component?: ComponentType<Record<string, unknown>>;
  svgIcon?: string;
  imageProps?: Record<string, unknown>;
  text?: ReactNode;
};

type LocationListProps = {
  selectedBrand?: Brand;
  loggedInUser?: User;
};

type Loader = (_loading: boolean) => void;

const TableServerComponent = TableServer as ComponentType<TableServerProps>;
const AutocompleteComponent =
  MUICustomAutocompleteSelect as ComponentType<AutocompleteProps>;
const ImageTextInlineComponent =
  RpImageTextInline as ComponentType<ImageTextInlineProps>;
const SvgIconComponent = RpSvgIcon as ComponentType<Record<string, unknown>>;

const LocationList = ({ selectedBrand, loggedInUser }: LocationListProps) => {
  const loader = useContext(LoaderContext) as Loader;
  const [filters, setFilters] = useState<LocationFilters>({
    pageSize: 10,
    pageIndex: 0,
    searchKey: "",
    sortBy: "",
    sortByDir: "",
    groupId: "",
  });
  const [groupList, setGroupList] = useState<GroupOption[]>([]);

  const reqData = useMemo(() => {
    return {
      id: selectedBrand?.id,
      userId: loggedInUser?.id,
      row: filters?.pageSize,
      page: filters?.pageIndex + 1,
      searchKey: filters?.searchKey || "",
      sortBy: filters?.sortBy || "",
      sortByDir: filters?.sortByDir || "",
      groupId: filters?.groupId || "",
    };
  }, [selectedBrand?.id, loggedInUser?.id, filters]);

  const { data, isLoading } = useFetcher(
    reqData,
    brandService.getBrandLocations,
    { revalidateOnFocus: false }
  ) as { data?: LocationListResponse; isLoading: boolean };

  useEffect(() => {
    loader(isLoading);
  }, [isLoading, loader]);

  const brandLocationsData = data?.result || [];
  const pageCount = data?.pageCount || 0;
  const recordsTotal = data?.recordsTotal || 0;

  useEffect(() => {
    if (data && data?.groupResult) {
      const groupList = data?.groupResult?.map((c) => ({
        label: c.group_name,
        value: c.id,
      }));
      setGroupList(groupList);
    }
  }, [data]);

  const handleFetchData = useCallback((data: FetchDataParams) => {
    const {
      pageIndex,
      pageSize,
      searchKey,
      filters: fetchFilters,
      sortBy,
      sortByDir,
    } = data;

    const reqData = {
      ...fetchFilters,
      pageSize: pageSize,
      pageIndex: pageIndex,
      searchKey: searchKey || "",
      sortBy,
      sortByDir,
      groupId: fetchFilters?.groupId || "",
    };

    setFilters((f) => ({ ...f, ...reqData }));
  }, []);

  const columns = useMemo<TableColumn[]>(() => {
    const columns: TableColumn[] = [
      {
        Header: "Location Name",
        accessor: "location_name",
        id: "location_name",
        width: 261,
        Cell: ({
          row: {
            original: {
              location_name,
              system_generated_location_id,
              fk_participant_id,
            },
          },
        }) => {
          return (
            <Link
              to={`/dashboard/location-report/${system_generated_location_id}/${fk_participant_id}`}
              className="link text-break"
            >
              <ImageTextInlineComponent
                component={SvgIconComponent}
                svgIcon="location_mark"
                imageProps={{
                  color: "primary",
                }}
                text={makeFirstCharCap(location_name)}
              />
            </Link>
          );
        },
      },
      {
        Header: "Participant Name",
        accessor: "participants_name",
        id: "participant_name",
        width: 215,
        Cell: ({
          row: {
            original: { participants_name },
          },
        }) => {
          return <span>{participants_name ? participants_name : "N/A"}</span>;
        },
      },
    ];

    if (selectedBrand && selectedBrand?.id !== BRAND_CONST.MS_BRAND_ID) {
      // if not Morgan Stanley
      columns.push({
        Header: () => <span className="text-center w-100">Live Campaigns</span>,
        accessor: "getTotalCampaignActive",
        Cell: ({ row: { original } }) => {
          return (
            <div className="text-center">
              <ImageTextInlineComponent
                component={SvgIconComponent}
                svgIcon="campaign"
                imageProps={{
                  color: Number(original.getTotalCampaignActive)
                    ? "primary"
                    : "contrastText",
                }}
                text={original.getTotalCampaignActive}
              />
            </div>
          );
        },
      });
      columns.push({
        Header: () => (
          <span className="text-center w-100">Total Campaigns</span>
        ),
        accessor: "getTotalCampaign",
        Cell: ({ row: { original } }) => {
          return <div className="text-left">{original.getTotalCampaign}</div>;
        },
      });
    }
    return columns;
  }, [selectedBrand]);

  const groupFilter = (
    setFilter: (_key: keyof LocationFilters, _value: string | number) => void,
    selectedValues?: Partial<LocationFilters>
  ) => {
    return (
      <div className="col-sm col-lg-3 col-xl-3">
        <AutocompleteComponent
          options={groupList}
          selectProps={{
            onChange: (v) => {
              setFilter("groupId", v);
            },
            value: selectedValues?.groupId,
          }}
          name="location-group"
          label="Groups"
          className="mt-1 py-1"
          size="large"
          all
        />
      </div>
    );
  };

  return (
    <div className="location-list">
      <TableServerComponent
        columns={columns}
        data={brandLocationsData}
        showSearch={true}
        fetchData={handleFetchData}
        isLoading={isLoading}
        pageCount={pageCount}
        filters={groupFilter}
        useLayout={false}
        hideSearchLabel={false}
        totalRecords={recordsTotal}
      />
    </div>
  );
};

export default LocationList;
